/**
 * Eric (2026-02) — Ticket "polling re_optimize_status + progress bar".
 *
 * Panneau live de suivi du job bulk-reoptimize sur `VideoQueuePage`.
 * - Poll `GET /api/videos/bulk-reoptimize/status` toutes les 3 s tant que
 *   `status === "running"`.
 * - Affiche : barre de progression, compteurs (succès / échecs / traités),
 *   ETA calculé côté client (vitesse × restant), vidéo en cours, liste
 *   collapsible des 10 dernières vidéos avec statut + timestamp relatif.
 * - À la fin (status "complete") : bandeau vert. L'utilisateur peut fermer
 *   manuellement ; sinon il disparaît automatiquement après 12 secondes.
 *
 * data-testid exposés (pour Playwright) :
 *   bulk-progress-panel, bulk-progress-bar, bulk-progress-counter-processed,
 *   bulk-progress-counter-succeeded, bulk-progress-counter-failed,
 *   bulk-progress-eta, bulk-progress-current-video,
 *   bulk-progress-details-toggle, bulk-progress-recent-list,
 *   bulk-progress-recent-item-<id>, bulk-progress-close-btn,
 *   bulk-progress-complete-banner
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  CaretDown,
  CaretUp,
  CheckCircle,
  CircleNotch,
  Wrench,
  X,
  XCircle,
} from "@phosphor-icons/react";

import { API_BASE as API } from "../../lib/apiBase";

const POLL_INTERVAL_MS = 3000;
const AUTO_DISMISS_AFTER_COMPLETE_MS = 12000;

function fmtDuration(secs) {
  if (secs == null || !isFinite(secs) || secs < 0) return "—";
  if (secs < 60) return `${Math.round(secs)} s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  if (m < 60) return s > 0 ? `${m} min ${s} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

function fmtRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diffS = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffS < 60) return `il y a ${diffS} s`;
  const m = Math.floor(diffS / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  return `il y a ${h} h`;
}

export default function BulkReoptimizeProgress({ refreshTrigger = 0 }) {
  const [job, setJob] = useState(null);
  const [dismissedJobId, setDismissedJobId] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const pollRef = useRef(null);
  const dismissTimerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/videos/bulk-reoptimize/status`,
        { withCredentials: true },
      );
      setJob(data?.job || null);
    } catch (e) {
      // Silencieux — on ne veut pas polluer l'UI si le backend hoquette.
      console.warn("[BulkReoptimizeProgress] status fetch failed", e?.message);
    }
  }, []);

  // Fetch initial + polling tant que running
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus, refreshTrigger]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (job?.status === "running") {
      pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [job?.status, fetchStatus]);

  // Auto-dismiss 12 s après complétion
  useEffect(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    if (job && (job.status === "complete" || job.status === "failed")) {
      dismissTimerRef.current = setTimeout(() => {
        setDismissedJobId(job._id);
      }, AUTO_DISMISS_AFTER_COMPLETE_MS);
    }
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
    };
  }, [job]);

  // Cas d'affichage : pas de job OU job dismissé OU job trop vieux (>10 min)
  if (!job) return null;
  if (dismissedJobId === job._id) return null;
  if (job.status !== "running") {
    const finishedAt = job.finished_at ? new Date(job.finished_at).getTime() : 0;
    if (finishedAt && Date.now() - finishedAt > 10 * 60 * 1000) return null;
  }

  const total = job.total || 0;
  const processed = job.processed || 0;
  const succeeded = job.succeeded || 0;
  const failed = job.failed || 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const isRunning = job.status === "running";
  const isComplete = job.status === "complete" || (!isRunning && processed >= total && total > 0);

  // ETA : vitesse (vidéos / s) × restant. Nécessite ≥ 1 vidéo traitée +
  // un started_at valide. On clamp à 24h pour éviter les valeurs absurdes
  // en début de job (1 vidéo lente extrapolée).
  let etaSecs = null;
  if (isRunning && processed > 0 && total > processed && job.started_at) {
    const startedMs = new Date(job.started_at).getTime();
    const elapsedS = Math.max(1, (Date.now() - startedMs) / 1000);
    const speed = processed / elapsedS; // vidéos par seconde
    if (speed > 0) {
      etaSecs = Math.min(24 * 3600, (total - processed) / speed);
    }
  }

  const recent = Array.isArray(job.recent_videos) ? job.recent_videos.slice().reverse() : [];

  return (
    <div
      className={`border rounded-xl overflow-hidden shadow-sm ${
        isComplete
          ? failed > 0
            ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
            : "border-teal-300 bg-teal-50 dark:bg-teal-950/20"
          : "border-[#002FA7]/30 bg-blue-50 dark:bg-blue-950/20"
      }`}
      data-testid="bulk-progress-panel"
    >
      <div className="px-5 py-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isComplete
                  ? failed > 0
                    ? "bg-amber-100 dark:bg-amber-900/40"
                    : "bg-teal-100 dark:bg-teal-900/40"
                  : "bg-[#002FA7]/10"
              }`}
            >
              {isRunning ? (
                <CircleNotch size={18} className="animate-spin text-[#002FA7]" />
              ) : failed > 0 ? (
                <XCircle size={18} className="text-amber-600" weight="fill" />
              ) : (
                <CheckCircle size={18} className="text-teal-600" weight="fill" />
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm">
                {isRunning
                  ? "Ré-optimisation en cours"
                  : failed > 0
                    ? `Ré-optimisation terminée avec ${failed} échec${failed > 1 ? "s" : ""}`
                    : "Ré-optimisation terminée"}
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                <span data-testid="bulk-progress-counter-processed">
                  {processed} / {total}
                </span>{" "}
                vidéos traitées
                {isRunning && etaSecs != null && (
                  <>
                    {" · "}
                    <span data-testid="bulk-progress-eta">
                      ETA ~{fmtDuration(etaSecs)}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <span
              className="text-xs font-semibold px-2 py-1 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 inline-flex items-center gap-1"
              data-testid="bulk-progress-counter-succeeded"
              title="Vidéos ré-optimisées avec succès"
            >
              <CheckCircle size={12} weight="fill" /> {succeeded} réussies
            </span>
            <span
              className="text-xs font-semibold px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 inline-flex items-center gap-1"
              data-testid="bulk-progress-counter-failed"
              title="Vidéos dont FFmpeg n'a pas réussi la ré-optimisation"
            >
              <XCircle size={12} weight="fill" /> {failed} échouées
            </span>
            {!isRunning && (
              <button
                type="button"
                onClick={() => setDismissedJobId(job._id)}
                className="w-7 h-7 rounded-lg text-zinc-500 hover:bg-zinc-200/60 dark:hover:bg-zinc-800/60 flex items-center justify-center"
                data-testid="bulk-progress-close-btn"
                title="Fermer"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Barre de progression */}
        <div
          className="mt-3 h-2 bg-white/70 dark:bg-black/30 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          data-testid="bulk-progress-bar"
        >
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${
              isComplete
                ? failed > 0
                  ? "bg-gradient-to-r from-amber-500 to-amber-400"
                  : "bg-gradient-to-r from-teal-600 to-teal-400"
                : "bg-gradient-to-r from-[#002FA7] to-blue-400"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex justify-between text-[11px] text-zinc-500 mt-1 tabular-nums">
          <span>{pct}%</span>
          {isRunning && job.started_at && (
            <span>Depuis {fmtDuration((Date.now() - new Date(job.started_at).getTime()) / 1000)}</span>
          )}
          {!isRunning && job.finished_at && (
            <span>Terminé {fmtRelative(job.finished_at)}</span>
          )}
        </div>

        {/* Vidéo en cours */}
        {isRunning && job.current_video?.title && (
          <div
            className="mt-3 flex items-center gap-2 text-xs text-zinc-700 dark:text-zinc-300 bg-white/60 dark:bg-black/20 px-3 py-2 rounded-lg"
            data-testid="bulk-progress-current-video"
          >
            <Wrench size={12} className="text-[#002FA7] flex-shrink-0" />
            <span className="font-medium flex-shrink-0">En cours :</span>
            <span className="truncate" title={job.current_video.title}>
              {job.current_video.title}
            </span>
          </div>
        )}

        {/* Détails collapsibles : 10 dernières vidéos */}
        {recent.length > 0 && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:text-[#002FA7] inline-flex items-center gap-1"
              data-testid="bulk-progress-details-toggle"
              aria-expanded={showDetails}
            >
              {showDetails ? <CaretUp size={12} /> : <CaretDown size={12} />}
              Détails ({recent.length} dernière{recent.length > 1 ? "s" : ""} vidéo{recent.length > 1 ? "s" : ""})
            </button>
            {showDetails && (
              <ul
                className="mt-2 divide-y divide-zinc-200/70 dark:divide-zinc-800 bg-white/60 dark:bg-black/20 rounded-lg overflow-hidden"
                data-testid="bulk-progress-recent-list"
              >
                {recent.map((r) => (
                  <li
                    key={`${r.id}-${r.finished_at}`}
                    className="px-3 py-1.5 flex items-center gap-2 text-xs"
                    data-testid={`bulk-progress-recent-item-${r.id}`}
                  >
                    {r.status === "success" ? (
                      <CheckCircle size={12} weight="fill" className="text-teal-500 flex-shrink-0" />
                    ) : (
                      <XCircle size={12} weight="fill" className="text-red-500 flex-shrink-0" />
                    )}
                    <span className="truncate flex-1" title={r.title}>{r.title}</span>
                    {r.status === "failed" && r.error && (
                      <span className="text-[10px] text-red-500 truncate max-w-[180px] hidden md:inline" title={r.error}>
                        {r.error}
                      </span>
                    )}
                    <span className="text-[10px] text-zinc-500 flex-shrink-0 tabular-nums">
                      {fmtRelative(r.finished_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Bandeau de complétion accessibilité (annonce visuelle) */}
      {isComplete && (
        <div className="sr-only" data-testid="bulk-progress-complete-banner">
          Ré-optimisation terminée : {succeeded} réussies, {failed} échouées.
        </div>
      )}
    </div>
  );
}
